import Controller from '@ember/controller';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { allSettled, dropTask, restartableTask, timeout } from 'ember-concurrency';
import { showModal } from 'sh-www/utils/modal-controls';
import { DASHBOARD_MAX_ANIMATION_DURATION_MS, DASHBOARD_TYPES, SPACER_IMAGE_URL } from 'sh-www/constants/dashboard';
import { DATA_ATTRIBUTES } from 'sh-www/constants/data-attributes';
import { SELLER_ACTION_TYPES } from 'sh-www/models/user-action-item';
import { CAMPAIGN_IDS } from 'sh-www/models/campaign';

const CLASS_NAME = 'SH-r-dashboard';

const DASHBOARD_SELLER_DEFAULT_INCLUDES = [
  'seller-application-stats',
  'seller-posting-stats',
  'seller-project-stats',
  'seller-latest-report-stats',
  'company-seller-connections.company:in_network_only',
];

const DASHBOARD_SELLER_WORK_SAMPLE_INCLUDES = [
  'projectdigests.projectdigest-verifications:verified_or_latest',
];

const ACTION_TYPE_TO_DATA_REF_HASH = {
  [SELLER_ACTION_TYPES.RECENT_PAYOUT_FAILED]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_PAYOUT_FAILED_CARD,
  [SELLER_ACTION_TYPES.PROJECT_AUTOCOMPLETES_TODAY]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_PROJECT_AUTOCOMPLETES_TODAY_CARD,
  [SELLER_ACTION_TYPES.UPCOMING_PAYOUT_CANNOT_BE_PROCESSED]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_PAYOUT_CANNOT_PROCESS_CARD,
  [SELLER_ACTION_TYPES.PROJECT_AUTOCOMPLETES_TOMORROW]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_PROJECT_AUTOCOMPLETES_TOMORROW_CARD,
  [SELLER_ACTION_TYPES.PROJECT_CONFIRM_COMPLETION]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_PROJECT_CONFIRM_COMPLETION_CARD,
  [SELLER_ACTION_TYPES.PROJECT_ACCEPT_ADVANCE]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_ACCEPT_ADVANCE_CARD,
  [SELLER_ACTION_TYPES.APPLICATION_SIGN_CONTRACT]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_APPLICATION_SIGN_CONTRACT_CARD,
  [SELLER_ACTION_TYPES.PROJECT_SIGN_CONTRACT]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_PROJECT_SIGN_CONTRACT_CARD,
  [SELLER_ACTION_TYPES.APPLICATION_REVIEW_OFFER]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_APPLICATION_REVIEW_OFFER_CARD,
  [SELLER_ACTION_TYPES.PROJECT_REVIEW_OFFER]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_PROJECT_REVIEW_OFFER_CARD,
  [SELLER_ACTION_TYPES.POSTING_LAST_DAY]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_POSTING_LAST_DAY_CARD,
  [SELLER_ACTION_TYPES.APPLICATION_SAVED_DEADLINE]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_APPLICATION_SAVED_DEADLINE_CARD,
  [SELLER_ACTION_TYPES.APPLICATION_INVITED_DEADLINE]: DATA_ATTRIBUTES.REF.SELLER_DASHBOARD_ACTION_APPLICATION_INVITED_DEADLINE_CARD,
};

class SellerDashboardController extends Controller {
  @service('commands/user-commands') userCommandService;
  @service('commands/support-commands') supportCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('error-tracker') errorTracker;
  @service session;
  @service media;
  @service store;
  @service api;

  actionItems = [];
  latestSupportArticle = null;
  user = null;

  get className() {
    return CLASS_NAME;
  }

  get dashboardType() {
    return DASHBOARD_TYPES.SELLER;
  }

  get spacerImageUrl() {
    return SPACER_IMAGE_URL;
  }

  @computed('user.companySellerConnections.@each.{invited,completedProjects,activeProjects}')
  get inNetworkCompanyConnections() {
    const inviterCompanies = this.user?.companySellerConnections?.reduce((accum, companySellerConnection) => {
      if (companySellerConnection.invited) {
        accum.add(companySellerConnection);
      }

      return accum;
    }, new Set());

    if (!inviterCompanies) return [];

    return [...inviterCompanies].sort((a, b) => {
      return (b.activeProjects + b.completedProjects) - (a.activeProjects + a.completedProjects);
    });
  }

  @action
  mapActionTypeToDataRef(actionType) {
    return ACTION_TYPE_TO_DATA_REF_HASH[actionType];
  };

  @action
  onReferAClientClicked() {
    const modalModel = {
      campaign: this.loadCampaign.last.value,
    };

    showModal(this, 'refer-client-modal', modalModel);
  }

  @restartableTask
  *bootstrapPage() {
    yield Promise.race([
      this.loadModuleData.perform(),
      timeout(DASHBOARD_MAX_ANIMATION_DURATION_MS),
    ]);
  }

  @restartableTask
  *loadModuleData() {
    const { id } = this.session.currentUser;

    yield allSettled([
      this.getUser.perform(id, DASHBOARD_SELLER_DEFAULT_INCLUDES),
      this.getActionItems.perform(id),
      this.getLatestSupportArticle.perform(),
      this.loadCampaign.perform(),
    ]);

    if (this.user && !this.user.isVetted && isEmpty(this.user.companySellerConnections)) {
      yield this.getUser.perform(id, DASHBOARD_SELLER_WORK_SAMPLE_INCLUDES);
    }
  }

  @dropTask
  *getUser(userId, includes) {
    const user = yield this.userCommandService.getUser(userId, includes);
    this.set('user', user);
  }

  @dropTask
  *getActionItems(userId) {
    try {
      const actionItems = yield this.userCommandService.getUserActionItems(userId);
      this.set('actionItems', actionItems);
    } catch (error) {
      this.errorTracker.error('getActionItems -- Could not fetch user action items', this);
    }
  }

  @dropTask
  *getLatestSupportArticle() {
    this.set('latestSupportArticle', {
      body: '',
      htmlUrl: 'https://help.glimmer.io/en/articles/8557480-navigating-your-creator-dashboard',
      name: 'Navigating your Creator Dashboard',
      title: 'Navigating your Creator Dashboard',
      categoryName: 'For Creators',
      sectionName: 'Creator Profile',
    });

    // try {
    //   const options = {
    //     params: {
    //       category: 'freelancer',
    //       per_page: 1,
    //       sideloads: ['categories', 'sections'],
    //     },
    //   };
    //
    //   const response = yield this.supportCommandService.loadHelpCenterArticles(options);
    //   const [article] = response.articles;
    //   const [category] = response.categories;
    //   const [section] = response.sections;
    //
    //   this.set('latestSupportArticle', {
    //     body: article['body'],
    //     htmlUrl: article['html_url'],
    //     name: article['name'],
    //     title: article['title'],
    //     categoryName: category.name,
    //     sectionName: section.name,
    //   });
    // } catch(e) {
    //   this.errorTracker.error('getLatestSupportArticle -- Could not fetch latest support article', this);
    // }
  }

  @dropTask
  *loadCampaign() {
    try {
      const response = yield this.api.get(`campaigns/${CAMPAIGN_IDS.SELLER_REFER_CLIENT_DEFAULT_CAMPAIGN}`);
      this.store.pushPayload(response);
      return this.store.peekRecord('campaign', response.data.id);
    } catch(e) {
      this.errorTracker.error('loadCampaign -- Could not fetch campgin', this);
    }
  }
}

export default SellerDashboardController;
